import React, {useCallback, useRef} from 'react';
import {useActualRef} from './useActualRef';

export interface MouseDragOptions {
	onStart?(event: MouseEvent): void;
	onStop?(event?: MouseEvent): void;
	onMove(event: MouseEvent, meta: {totalDeltaX: number; totalDeltaY: number}): void;

	/** The minimum distance that the mouse must shift in the "clicked" state to either side to start dragging */
	minimumXDistance?: number;

	/** @see minimumXDistance */
	minimumYDistance?: number;

	/**
	 * Start will be triggered only if an event button (event.button) is equal to the value defined here
	 * Pass "undefined" to receive any button values
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
	 * @default 0 - Only left mouse button
	 */
	onlyButton?: number;
}

/**
 * Provides tracking of the start and subsequent dragging an element.
 * It is useful mainly because it allows you to use dynamic handler functions.
 * And also calculates the total distance of the mouse pointer movement relative to the start of the drag
 * (useful since summing the distance for each mouse movement event gives an inaccuracy)
 */
export function useMouseDrag(props: MouseDragOptions) {
	const {onStart, onStop, onMove, onlyButton = 0, minimumXDistance = 0, minimumYDistance = 0} = props;

	// Use useActualRef to have an actual value of callbacks without the native event handlers update
	const onStartRef = useActualRef(onStart);
	const onStopRef = useActualRef(onStop);
	const onMoveRef = useActualRef(onMove);

	// Save an initial mouse position to get an total move distance
	const initialMousePosition = useRef<[number, number] | null>(null);
	const wasMove = useRef(false);

	const handleMouseMove = useCallback((event: MouseEvent) => {
		// TODO check is a new coordinates is not the same as previous (actual for dome moused on Windows)

		// Calculate a total delta from the start of moving point to obtain accurate value of the required scroll distance.
		// Because scrolling by delta from the last move event is inaccurate.
		const [initialX, initialY] = initialMousePosition.current!;
		const totalDeltaX = event.clientX - initialX;
		const totalDeltaY = event.clientY - initialY;

		// On first move event report an move start
		if (!wasMove.current) {
			// If no minimum distance reached, do not trigger start callback
			if (Math.abs(totalDeltaX) < minimumXDistance && Math.abs(totalDeltaY) < minimumYDistance) {
				return;
			}

			wasMove.current = true;

			// Trigger the start callback
			if (onStartRef.current) {
				onStartRef.current(event);
			}
			return;
		}

		// Trigger the callback
		onMoveRef.current(event, {totalDeltaX, totalDeltaY});
	}, []);

	const handleMouseUp = useCallback((event?: MouseEvent) => {
		initialMousePosition.current = null;
		wasMove.current = false;

		// Dispose global mouse events
		document.documentElement.removeEventListener('mouseup', handleMouseUp);
		document.documentElement.removeEventListener('mousemove', handleMouseMove);

		// Trigger the callback
		if (onStopRef.current) {
			onStopRef.current(event);
		}
	}, []);

	const handleMouseDown = useCallback((event: React.MouseEvent | MouseEvent) => {
		if (onlyButton !== undefined && onlyButton !== event.button) {
			return;
		}

		// Prevent default to avoid text context selection
		event.preventDefault();

		// Save initial (at the drag start) values of the mouse position
		initialMousePosition.current = [event.clientX, event.clientY];

		// Listen global mouse events
		document.documentElement.addEventListener('mouseup', handleMouseUp);
		document.documentElement.addEventListener('mousemove', handleMouseMove);
	}, []);

	// Allow imperative break drag process
	const handleForceBreak = useCallback(() => handleMouseUp(), [handleMouseUp]);

	return {
		handleMouseDown,
		forceBreak: handleForceBreak,
	};
}

useMouseDrag.displayName = 'useMouseDrag';
